import { MatchController } from "../../MatchController";
import { PenaltyManager } from "../../PenaltyManager";
import { ForwardPlayer } from "../../physics/players/ForwardPlayer";
import { PenaltyGoalKeeper } from "../../physics/pegs/PenaltyGoalKeeper";
import { BackgroundManager } from "../../BackgroundManager";
import { HUD } from "../../HUD/HUD";
import { FieldBoundaries } from "../../physics/FieldBoundaries";
import { PenaltyCurtain } from "./PenaltyCurtain";
import { Ball } from "../../physics/Ball";
import { GameVars } from "../../GameVars";
import { GameConstants } from "../../GameConstants";
import { AudioManager, SoundTrack } from "../../AudioManager";
import { GameManager } from "../../GameManager";
import { Player } from "../../physics/players/Player";

export class PenaltyFootballFieldState extends Phaser.State {

    public static currentInstance: PenaltyFootballFieldState;

    public matchController: MatchController;
    public penaltyModel: PenaltyManager;
    public forward: ForwardPlayer;

    private penaltyGoalKeeper: PenaltyGoalKeeper;
    private backgroundManager: BackgroundManager;
    private hud: HUD;
    private fieldBoundaries: FieldBoundaries;
    private blackLayer: Phaser.Image;
    private penaltyCurtain: PenaltyCurtain;
    private ballGroup: Phaser.Group;

    public create(): void {

        PenaltyFootballFieldState.currentInstance = this;

        this.backgroundManager = new BackgroundManager(this.game, BackgroundManager.PENALTY);
        this.add.existing(this.backgroundManager);

        this.matchController = new MatchController(this.game);

        this.initialize();
    }

    public shutdown(): void {

        PenaltyFootballFieldState.currentInstance = null;

        this.matchController.destroy();

        super.shutdown();
    }

    public onMatchStart(): void {

        this.hud.start();
    }

    public shootPenalty(px: number, py: number, angle: number): void {

        let ball = new Ball(this.game, px, py);
        ball.initPhysics();
        this.ballGroup.add(ball);

        let angleInRadians = Math.PI / 180 * angle;

        let force = 1500;

        ball.body.velocity.x = force * Math.cos(angleInRadians);
        ball.body.velocity.y = force * Math.sin(angleInRadians);
    }

    public onMatchEnd(): void {

        this.game.physics.p2.pause();

        if (GameVars.matchResult === GameConstants.VICTORY) {

            this.backgroundManager.onGoal();
            this.hud.onPenaltyScored();
            this.penaltyGoalKeeper.onGoal();

            AudioManager.getInstance().stopMusic(SoundTrack.PENALTY);
            AudioManager.getInstance().playEffect("celebracion_victoria");
        }

        let delayNextScene = GameVars.matchJustUnlocked ? 5000 : 900;

        this.game.time.events.add(delayNextScene, function (): void {
            GameManager.showMatchResult();
        }, this);
    }

    private initialize(): void {

        this.matchController.init(this.backgroundManager.backgroundImage);

        this.game.physics.p2.reset();
        this.game.physics.p2.setBoundsToWorld(false, false, false, false);
        this.game.physics.p2.resume();

        // debe haber sido creado un state para que los materiales funcionen
        GameVars.getInstance().createMaterials();

        this.ballGroup = this.add.group();
        this.ballGroup.scale.y = GameVars.scaleY * GameVars.fieldCorrectionScaleY;

        this.forward = new ForwardPlayer(this.game, 380, 350, true, null, Player.STILL);
        PenaltyManager.init(this.game, this.forward);
        this.ballGroup.add(this.forward);

        this.fieldBoundaries = new FieldBoundaries(this.game);
        this.add.existing(this.fieldBoundaries);

        this.penaltyGoalKeeper = new PenaltyGoalKeeper(this.game, 200, 650 / (GameVars.scaleY * GameVars.fieldCorrectionScaleY));
        this.ballGroup.add(this.penaltyGoalKeeper);

        this.add.image(155, 760, "texture_atlas_1", "goal");

        this.penaltyCurtain = new PenaltyCurtain(this.game);
        this.add.existing(this.penaltyCurtain);

        this.hud = new HUD(this.game);
        this.add.existing(this.hud);

        this.blackLayer = this.add.sprite(0, 0, this.game.cache.getBitmapData("black_square"));
        this.blackLayer.scale.set(12, 16);
        this.blackLayer.alpha = 0;

        this.add.tween(this.blackLayer).to({ alpha: 0 }, 700, Phaser.Easing.Cubic.Out, true)
            .onComplete.add(function (): void {
                this.blackLayer.destroy();
            }, this);

        AudioManager.getInstance().playMusic(SoundTrack.PENALTY);
    }

    private goToScene(nextScene: string): void {

        this.blackLayer = this.add.sprite(0, 0, this.game.cache.getBitmapData("black_square"));
        this.blackLayer.scale.set(12, 16);
        this.blackLayer.alpha = 0;

        let delay: number;
        if (GameVars.matchResult === GameConstants.VICTORY) {
            delay = 2200;
        } else {
            delay = 700;
        }

        AudioManager.getInstance().stopMusic(SoundTrack.PENALTY);

        this.add.tween(this.blackLayer).to({ alpha: 1 }, delay, Phaser.Easing.Cubic.Out, true)
            .onComplete.add(function (): void {
                this.game.state.start(nextScene, true, false);
            }, this);

        this.game.time.events.add(delay, function (): void {
            this.game.state.start(nextScene, true, false);
        }, this);
    }
}
